import React, { useState, useEffect } from 'react';
import {
    Container, Paper, TextField, Autocomplete, Button, Typography, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
    IconButton, Snackbar, Alert, Box, ThemeProvider, createTheme, MenuItem,
    Collapse, Chip, CircularProgress, Card, CardContent, Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MapboxClient from '@mapbox/mapbox-sdk/services/geocoding';
import axios from 'axios';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const geocodingClient = MapboxClient({ accessToken: MAPBOX_TOKEN });
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const theme = createTheme({
    palette: {
        primary: { main: '#11224d' },
        secondary: { main: '#E1AD01' },
        background: { default: '#f3f4f6', paper: '#fff' },
    },
    typography: { fontSize: 12 },
});

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5),
    backgroundColor: '#fff',
    borderRadius: theme.spacing(1),
}));

const StyledCard = styled(Card)(({ theme }) => ({
    marginTop: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
}));

const RADIUS_OPTIONS = [50, 100, 150, 200, 300, 500];

let debounceTimer;
const suggestionsCache = new Map();

const fetchMapboxSuggestions = async (queryText) => {
    if (!queryText || queryText.trim().length < 3) return [];
    const cacheKey = queryText.trim().toLowerCase();
    if (suggestionsCache.has(cacheKey)) return suggestionsCache.get(cacheKey);

    return new Promise((resolve) => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
            try {
                const res = await geocodingClient.forwardGeocode({
                    query: queryText,
                    autocomplete: true,
                    limit: 6,
                    countries: ['US'],
                    types: ['place']
                }).send();

                const results = (res.body.features || [])
                    .filter(f => !f.place_type.includes('region'))
                    .map(feature => {
                        const city = feature.text || '';
                        const stateObj = feature.context?.find(c => c.id.startsWith('region'));
                        const state = stateObj?.short_code
                            ? stateObj.short_code.split('-')[1].toUpperCase()
                            : (stateObj?.text || '');
                        return {
                            ...feature,
                            place_name: city && state ? `${city}, ${state}` : city || state
                        };
                    });
                suggestionsCache.set(cacheKey, results);
                resolve(results);
            } catch (err) {
                console.error('Mapbox Autocomplete error:', err);
                resolve([]);
            }
        }, 300);
    });
};

const getOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    if (!option?.place_name) return '';
    return option.place_name;
};

const formatEmail = (email) => (email ? String(email).trim().toLowerCase() : '');

const formatCity = (city) => {
    if (!city) return '';
    const trimmed = String(city).trim().toLowerCase();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const telHref = (phone) => {
    if (!phone) return '';
    const digits = String(phone).replace(/[^0-9+]/g, '');
    return `tel:${digits}`;
};

const ctaLinkSx = {
    color: '#4dabf5',
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
};

// Note: no phone column here at header level — signup shippers never have one,
// and for CSV shippers it's shown in the expanded detail card instead so the
// header layout stays identical for both source types.
const HEADER_COLUMNS = [
    { key: 'company_name', label: 'Company Name', width: 260 },
    { key: 'phy_city', label: 'City', width: 160 },
    { key: 'phy_state', label: 'State', width: 90 },
    { key: 'email', label: 'Email', width: 240 },
];

const DetailSection = ({ title, children }) => (
    <Box sx={{ pr: 2 }}>
        <Typography
            variant="subtitle2"
            sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#11224d', mb: 0.5 }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

function ShipperResultRow({ row, isFavorite, onAddFavorite }) {
    const [open, setOpen] = useState(false);

    const email = formatEmail(row.email);
    const city = formatCity(row.phy_city);
    const isSignup = row.source === 'signup';

    return (
        <>
            <TableRow
                hover
                onClick={() => setOpen(!open)}
                sx={{
                    cursor: 'pointer',
                    '& td': { position: 'relative' },
                    '& td:not(:last-child)::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '25%',
                        height: '50%',
                        borderRight: '1px solid #ddd',
                    },
                }}
            >
                <TableCell sx={{ fontWeight: 'bold' }}>
                    {row.company_name || '-'}
                    {isSignup && (
                        <Chip
                            label="Verified"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
                        />
                    )}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{city || '-'}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{row.phy_state || '-'}</TableCell>
                <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {email ? (
                        <a href={`mailto:${email}`} style={ctaLinkSx}>{email}</a>
                    ) : '-'}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={isFavorite ? 'Saved' : 'Add to favorites'}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => !isFavorite && onAddFavorite(row)}
                                sx={{
                                    color: isFavorite ? '#E1AD01' : 'action.active',
                                    cursor: isFavorite ? 'default' : 'pointer',
                                }}
                            >
                                {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <StyledCard>
                            <CardContent sx={{ p: 1.5 }}>
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem', color: '#11224d' }}
                                >
                                    {row.company_name || 'Unnamed Company'}
                                </Typography>
                                <Divider sx={{ my: 1 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ flex: 1, minWidth: 200, pr: 2, mb: 2 }}>
                                        <DetailSection title="Company">
                                            {!isSignup && (
                                                <Typography variant="body2">
                                                    <span style={{ fontWeight: 'bold' }}>Industry:</span>{' '}
                                                    {row.industry || '-'}
                                                </Typography>
                                            )}
                                            {!isSignup && (
                                                <Typography variant="body2">
                                                    <span style={{ fontWeight: 'bold' }}>Website:</span>{' '}
                                                    {row.website ? (
                                                        <a href={row.website} target="_blank" rel="noreferrer" style={ctaLinkSx}>
                                                            {row.website}
                                                        </a>
                                                    ) : '-'}
                                                </Typography>
                                            )}
                                            {!isSignup && (
                                                <Typography variant="body2">
                                                    <span style={{ fontWeight: 'bold' }}>Contact Name:</span>{' '}
                                                    {row.contact_name || '-'}
                                                </Typography>
                                            )}
                                            {isSignup && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Registered ShipperDock account
                                                </Typography>
                                            )}
                                        </DetailSection>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 200, pr: 2, mb: 2 }}>
                                        <DetailSection title="Contact">
                                            {/* Phone intentionally omitted for signup shippers */}
                                            {/* {!isSignup && (
                                                <Typography variant="body2">
                                                    <span style={{ fontWeight: 'bold' }}>Phone:</span>{' '}
                                                    {row.phone ? (
                                                        <a href={telHref(row.phone)} style={ctaLinkSx}>{row.phone}</a>
                                                    ) : '-'}
                                                </Typography>
                                            )} */}
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Email:</span>{' '}
                                                {email ? (
                                                    <a href={`mailto:${email}`} style={ctaLinkSx}>{email}</a>
                                                ) : '-'}
                                            </Typography>
                                        </DetailSection>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 200, mb: 2 }}>
                                        <DetailSection title="Location">
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Street:</span>{' '}
                                                {row.phy_street || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>City:</span>{' '}
                                                {city || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>State:</span>{' '}
                                                {row.phy_state || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Zip:</span>{' '}
                                                {row.phy_zip || '-'}
                                            </Typography>
                                            {row.distance_miles != null && (
                                                <Typography variant="body2">
                                                    <span style={{ fontWeight: 'bold' }}>Distance:</span>{' '}
                                                    {Number(row.distance_miles).toFixed(1)} mi
                                                </Typography>
                                            )}
                                        </DetailSection>
                                    </Box>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

/**
 * SearchShippers — used by both Broker and Carrier dashboards.
 * Hits /api/search/shipper. Results merge admin-posted CSV shippers
 * and real signed-up shipper accounts (no phone shown for signups).
 */
const SearchShippers = () => {
    const [formOpen, setFormOpen] = useState(true);
    const [originInput, setOriginInput] = useState('');
    const [originOptions, setOriginOptions] = useState([]);
    const [originCoords, setOriginCoords] = useState(null);
    const [radius, setRadius] = useState(150);
    const [results, setResults] = useState([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [favoriteIds, setFavoriteIds] = useState(new Set());
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const authHeaders = () => {
        const token = localStorage.getItem('jwtToken');
        return { Authorization: `Bearer ${token}` };
    };

    const fetchFavoriteIds = async () => {
        try {
            const res = await axios.get(`${API_BASE}/api/favorites/ids`, {
                headers: authHeaders(),
                params: { type: 'shipper' }
            });
            setFavoriteIds(new Set(res.data));
        } catch (err) {
            console.error('Failed to fetch favorite ids', err);
        }
    };

    useEffect(() => {
        fetchFavoriteIds();
    }, []);

    const handleSearch = async () => {
        if (!originCoords) {
            setError('Please select a valid origin from the suggestions.');
            return;
        }
        setLoading(true);
        setError(null);
        setSearched(true);
        try {
            const [lng, lat] = originCoords;
            const res = await axios.get(`${API_BASE}/api/search/shipper`, {
                headers: authHeaders(),
                params: { lat, lng, radius }
            });
            setResults(res.data.results || []);
        } catch (err) {
            console.error('Search shipper error:', err);
            setError(err.response?.data?.message || 'Search failed. Please try again.');
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddFavorite = async (record) => {
        try {
            await axios.post(`${API_BASE}/api/favorites`, {
                favorited_type: 'shipper',
                record
            }, { headers: authHeaders() });

            setFavoriteIds(prev => new Set(prev).add(`${record.source}:${record.id}`));
            setSnackbar({ open: true, message: `${record.company_name || 'Record'} added to favorites.`, severity: 'success' });
        } catch (err) {
            console.error('Add favorite error:', err);
            setSnackbar({ open: true, message: 'Could not save favorite.', severity: 'error' });
        }
    };

    const isSearchDisabled = !originInput.trim();

    return (
        <ThemeProvider theme={theme}>
            <style>
                {`
                  html, body, #root {
                    margin: 0;
                    padding: 0;
                    height: 100%;
                    overflow: hidden;
                  }
                `}
            </style>
            <Box
                sx={{
                    backgroundColor: '#f3f4f6',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Container
                    maxWidth="xl"
                    disableGutters
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        height: '100%',
                        overflow: 'hidden',
                        pl: 1,
                        pr: 1,
                        mt: 0,
                    }}
                >
                    <StyledPaper elevation={1} sx={{ mt: 1, mb: 0, p: 0 }}>
                        {!formOpen && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    px: 2,
                                    py: 1,
                                }}
                                onClick={() => setFormOpen(true)}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#11224d' }}>
                                    Search Shippers
                                </Typography>
                                <Tooltip title="Expand Form">
                                    <IconButton size="small">
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                                            <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                                            <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', mt: -1.5 }} />
                                        </Box>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        <Collapse in={formOpen}>
                            <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                        gap: 1,
                                        width: '100%',
                                        alignItems: { xs: 'stretch', sm: 'center' },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                            gap: { xs: 1.5, sm: 1.2 },
                                            alignItems: { xs: 'stretch', sm: 'center' },
                                            flex: 1,
                                        }}
                                    >
                                        <Autocomplete
                                            freeSolo
                                            size="small"
                                            options={originOptions}
                                            inputValue={originInput}
                                            getOptionLabel={getOptionLabel}
                                            onInputChange={async (e, value) => {
                                                setOriginInput(value || '');
                                                setOriginCoords(null);
                                                if (!value) {
                                                    setOriginOptions([]);
                                                    return;
                                                }
                                                const suggestions = await fetchMapboxSuggestions(value);
                                                setOriginOptions(suggestions);
                                            }}
                                            onChange={(e, selectedOption) => {
                                                if (!selectedOption || typeof selectedOption === 'string') return;
                                                setOriginInput(getOptionLabel(selectedOption));
                                                setOriginCoords(selectedOption.center || null);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Origin"
                                                    sx={{ background: '#fff' }}
                                                />
                                            )}
                                            sx={{ flex: { xs: '1 1 100%', md: 1 }, minWidth: { xs: '150px', md: '220px' } }}
                                        />

                                        <TextField
                                            select
                                            size="small"
                                            label="Radius"
                                            value={radius}
                                            onChange={(e) => setRadius(Number(e.target.value))}
                                            sx={{ width: { xs: '100%', sm: 140 }, background: '#fff' }}
                                        >
                                            {RADIUS_OPTIONS.map(r => (
                                                <MenuItem key={r} value={r}>{r} miles</MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            alignItems: 'center',
                                            ml: { xs: 0, sm: 1 },
                                            mt: { xs: 1, sm: 0 },
                                            flexShrink: 0,
                                            width: { xs: '100%', sm: 'auto' },
                                        }}
                                    >
                                        <Tooltip title="Collapse Form">
                                            <IconButton size="small" onClick={() => setFormOpen(false)} sx={{ color: 'primary.main', mr: 0.5 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                                                    <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                                                    <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', mt: -1.5 }} />
                                                </Box>
                                            </IconButton>
                                        </Tooltip>
                                        <Button
                                            variant="contained"
                                            onClick={handleSearch}
                                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                                            disabled={isSearchDisabled || loading}
                                            sx={{
                                                backgroundColor: '#11224d',
                                                color: 'white',
                                                flexShrink: 0,
                                                fontSize: '0.8rem',
                                                py: 1,
                                                px: 2,
                                                width: { xs: '100%', sm: 'auto' },
                                                '&:hover': { backgroundColor: '#0e1d3a' },
                                                '&.Mui-disabled': { backgroundColor: '#ccc', color: '#666' },
                                            }}
                                        >
                                            {loading ? 'Searching...' : 'Search'}
                                        </Button>
                                    </Box>
                                </Box>
                                {error && (
                                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                                        {error}
                                    </Alert>
                                )}
                            </Box>
                        </Collapse>
                    </StyledPaper>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            py: 1,
                            borderTop: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: '#f9fafc',
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {results.length} Results
                        </Typography>
                        {results.length > 0 && (
                            <Chip label={`Within ${radius} mi`} size="small" color="primary" variant="outlined" />
                        )}
                    </Box>

                    {loading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : searched && results.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No shippers found within {radius} miles of this origin.
                            </Typography>
                        </Box>
                    ) : results.length > 0 ? (
                        <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
                            <TableContainer component={Paper} sx={{ maxHeight: '100%', overflowX: 'auto', overflowY: 'auto' }}>
                                <Table
                                    size="small"
                                    sx={{
                                        tableLayout: 'fixed',
                                        '& .MuiTableCell-root': {
                                            padding: '7px 11px',
                                            fontSize: '0.7rem',
                                            lineHeight: 1.2,
                                        },
                                    }}
                                >
                                    <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#11224d' }}>
                                        <TableRow>
                                            {HEADER_COLUMNS.map((col) => (
                                                <TableCell
                                                    key={col.key}
                                                    sx={{
                                                        backgroundColor: '#11224d',
                                                        color: 'white',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 'bold',
                                                        padding: '6px 10px',
                                                        whiteSpace: 'nowrap',
                                                        width: col.width,
                                                        maxWidth: col.width,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        textAlign: 'center',
                                                        borderBottom: 'none',
                                                    }}
                                                >
                                                    {col.label}
                                                </TableCell>
                                            ))}
                                            <TableCell
                                                sx={{
                                                    backgroundColor: '#11224d',
                                                    color: 'white',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 'bold',
                                                    padding: '6px 10px',
                                                    whiteSpace: 'nowrap',
                                                    textAlign: 'center',
                                                    borderBottom: 'none',
                                                }}
                                            >
                                                Favorite
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {results.map((row) => (
                                            <ShipperResultRow
                                                key={`${row.source}-${row.id}`}
                                                row={row}
                                                isFavorite={favoriteIds.has(`${row.source}:${row.id}`)}
                                                onAddFavorite={handleAddFavorite}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : null}

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} sx={{ width: '100%' }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default SearchShippers;